import { Modal, Button } from '@platform/ui';
import { SEOUL_TZ } from '@platform/datetime';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { AnnualTerminationPreview } from '../api/subscription.api';

interface Props {
  open: boolean;
  preview: AnnualTerminationPreview | null;
  planName: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const won = (n: number) => `${n.toLocaleString('ko-KR')}원`;
const DATE_FMT = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  dateStyle: 'medium',
});
const fmt = (iso: string) => DATE_FMT.format(new Date(iso));

/**
 * 개인 연간 구독 중도해지(환불) 신청 — 환불·해지 정책 제5조 ④.
 *
 * 금액은 서버 프리뷰 값을 그대로 쓴다. 화면에서 다시 계산하면 운영자가 승인할 때 실제
 * 환불액과 갈라진다. 신청은 접수일 뿐이라 되돌릴 수 있지만(심사 전 철회), 승인되면 즉시
 * 종료되므로 그 결과를 확인 화면에 적어 둔다. 만류 문구·다단계 확인은 두지 않는다(해지 방해 금지).
 */
export function AnnualTerminationDialog({
  open,
  preview,
  planName,
  busy,
  onClose,
  onConfirm,
}: Props) {
  const eligible = preview?.eligible === true;
  const noRefund = !!preview && preview.refundAmount === 0;

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="중도해지 신청"
      description="연간 구독을 지금 해지하고 남은 금액을 환불받습니다."
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            돌아가기
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={!eligible || busy}
          >
            {busy ? '처리 중…' : '중도해지 신청'}
          </Button>
        </div>
      }
    >
      {!preview ? (
        <p className="py-6 text-center text-sm text-gray-400">
          환불 예정 금액을 계산하는 중…
        </p>
      ) : (
        <div className="space-y-4 text-sm text-gray-700">
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="mb-2 text-xs text-gray-500">
              {planName} · 이용 기간 {fmt(preview.periodStart)} ~{' '}
              {fmt(preview.periodEnd)}
            </p>
            <Row label="결제 금액" value={won(preview.paidAmount)} />
            <Row
              label={`이용 금액 (${preview.usedMonths}개월)`}
              value={`− ${won(preview.usageAmount)}`}
            />
            <p className="pl-3 text-xs text-gray-400">
              └ 할인 전 월 정가 {won(preview.monthlyListPrice)} ×{' '}
              {preview.usedMonths}개월
              {preview.monthlyListPriceFallback && ' (결제금액 ÷ 12 기준)'}
            </p>
            {preview.alreadyRefunded > 0 && (
              <Row
                label="이미 환불된 금액"
                value={`− ${won(preview.alreadyRefunded)}`}
              />
            )}
            <div className="mt-2 flex items-baseline justify-between border-t border-gray-200 pt-2">
              <span className="font-semibold text-gray-900">환불 예정 금액</span>
              <span className="text-lg font-bold text-gray-900">
                {won(preview.refundAmount)}
              </span>
            </div>
          </div>

          {noRefund && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              이용 금액이 결제 금액 이상이라 환불되는 금액이 없습니다. 만료일(
              {fmt(preview.periodEnd)})까지 계속 이용하려면 중도해지 대신 ‘해지
              신청’을 이용하세요.
            </p>
          )}

          <ul className="space-y-1 text-xs text-gray-600">
            <li>
              · 연간 구독은 할인된 요금으로 1년 이용을 약정한 상품이라, 이용한
              개월 수를 <b>할인 전 월 정가</b>로 계산해 뺍니다. 위약금은 없습니다.
            </li>
            <li>
              · 이용 개월은 신청일까지 세며, 1개월 미만은 1개월로 계산합니다.
            </li>
            <li>
              · 운영자 확인 후 <b>영업일 3일 이내</b> 결제수단으로 환불되고, 환불과
              함께 구독과 라이선스가 <b>즉시 종료</b>됩니다.
            </li>
            <li>· 처리 전에는 ‘구독해지 취소’로 신청을 철회할 수 있습니다.</li>
            <li>· 카드 취소 반영까지 카드사에 따라 영업일 3~5일이 걸릴 수 있습니다.</li>
          </ul>

          {!eligible && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {ineligibleMessage(preview)}
            </p>
          )}

          <p className="text-xs text-gray-400">
            자세한 기준은{' '}
            <a
              href={PATHS.LEGAL_REFUND}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline underline-offset-2"
            >
              환불·해지 정책
            </a>{' '}
            제5조에서 확인할 수 있습니다.
          </p>
        </div>
      )}
    </Modal>
  );
}

/** 불가 사유마다 다음에 할 일이 다르다 — 해지 신청으로 갈지, 고객지원으로 갈지. */
function ineligibleMessage(p: AnnualTerminationPreview): string {
  switch (p.reason) {
    case 'NOT_ANNUAL_INDIVIDUAL':
      return '연간 결제 개인 구독만 중도해지(환불)를 신청할 수 있습니다. 해지 신청을 이용해주세요.';
    case 'NOT_ACTIVE':
      return '이용 중인 구독만 신청할 수 있습니다. 결제 상태나 진행 중인 해지 신청을 확인해주세요.';
    case 'NO_REFUNDABLE_PAYMENT':
      return '이번 이용 기간의 결제 내역을 찾을 수 없습니다(이전 서비스에서 결제한 경우 등). 고객지원으로 문의해주세요.';
    case 'TERMINATED':
      return '이미 종료된 구독입니다.';
    default:
      return '지금은 중도해지를 신청할 수 없습니다. 고객지원으로 문의해주세요.';
  }
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-gray-500">{label}</span>
      <span>{value}</span>
    </div>
  );
}
